import app from 'flarum/forum/app';
import UserPage from 'flarum/forum/components/UserPage';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Button from 'flarum/common/components/Button';
import Link from 'flarum/common/components/Link';
import Avatar from 'flarum/common/components/Avatar';
import username from 'flarum/common/helpers/username';
import RequestsModal from './RequestsModal';
import RemoveFriendConfirmModal from './RemoveFriendConfirmModal';
import showFriendActionModal from '../showFriendActionModal';
const SEARCH_DEBOUNCE_MS = 700;
export default class FriendsPage extends UserPage {
  loading = true;
  loadingMore = false;
  friends = [];
  totalCount = 0;
  hasMore = false;
  search = '';
  searchTimer = null;
  friendsHidden = false;
  oninit(vnode) {
    super.oninit(vnode);
    this.loadUser(m.route.param('username'));
  }
  show(user) {
    super.show(user);
    this.loadFriends();

    // Click-through from a "friendshipRequested" notification (see that
    // component's href()). Deferred: show() can run inside Mithril's initial
    // render pass, and app.modal.show() there throws "Node is currently
    // being rendered to and thus is locked".
    const requestId = m.route.param('requestId');
    if (requestId && app.session.user?.id() === user.id()) {
      setTimeout(() => this.showRequestsModal(String(requestId)), 0);
    }
  }
  onsearch(value) {
    this.search = value;
    if (this.searchTimer) window.clearTimeout(this.searchTimer);
    this.searchTimer = window.setTimeout(() => this.loadFriends(), SEARCH_DEBOUNCE_MS);
  }
  async loadFriends(loadMore) {
    if (loadMore === void 0) {
      loadMore = false;
    }
    if (!this.user) return;
    if (loadMore) {
      this.loadingMore = true;
    } else {
      this.loading = true;
      this.friends = [];
      this.friendsHidden = false;
    }
    m.redraw();
    try {
      const filter = {
        user: this.user.id()
      };
      if (this.search.trim()) filter.q = this.search.trim();
      const results = await app.store.find('friendships', {
        filter,
        include: 'friend',
        sort: '-createdAt',
        page: {
          offset: loadMore ? this.friends.length : 0,
          limit: 24
        }
      }, undefined, {
        // A 403 here means friendship.viewOthers is off for this actor —
        // an expected, common state (most guests/members), not a real
        // error. Swap in the "hidden" empty state instead of Flarum's
        // red permission-denied alert; anything else (network failure,
        // 500) still falls through to the default handling.
        errorHandler: error => {
          if (error.status === 403) {
            this.friendsHidden = true;
            return;
          }
          return false;
        }
      });
      const newFriends = results;
      this.friends = loadMore ? [...this.friends, ...newFriends] : newFriends;
      const meta = results.payload?.meta;
      if (meta?.page?.total !== undefined) {
        this.totalCount = meta.page.total;
      } else if (meta?.total !== undefined) {
        this.totalCount = meta.total;
      } else if (!loadMore) {
        this.totalCount = this.friends.length;
      }
      this.hasMore = this.friends.length < this.totalCount;
    } catch (error) {
      if (!loadMore) {
        this.friends = [];
        this.totalCount = 0;
      }
    } finally {
      this.loading = false;
      this.loadingMore = false;
      m.redraw();
    }
  }
  content() {
    // Bare spinner, no header/search chrome, while the very first load is
    // still in flight — we don't yet know if this actor even has permission
    // to see anything, so nothing below should render prematurely (was
    // flashing the full "authorized" layout for ~500ms before friendsHidden
    // had a chance to flip on a 403).
    if (this.loading) {
      return m("div", {
        className: "FriendsPage"
      }, m(LoadingIndicator, null));
    }
    if (this.friendsHidden) {
      return m("div", {
        className: "FriendsPage"
      }, m("div", {
        className: "FriendsPage-emptyState"
      }, m("i", {
        className: "fas fa-user-friends FriendsPage-emptyIcon"
      }), m("p", null, app.translator.trans('forumaker-friendship.forum.user.friends_hidden'))));
    }
    const isOwnPage = !!this.user && app.session.user?.id() === this.user.id();
    const canModerate = app.forum.attribute('canModerateFriendships') === true;
    const canOpenRequests = isOwnPage ? app.forum.attribute('canAddFriends') === true : canModerate;
    const canRemove = isOwnPage || canModerate;

    // Viewing someone else's page (via friendship.viewOthers/moderate):
    // the actor's own relationship to *that user* — not to their friends
    // listed below — same add/remove control as the profile dropdown.
    const showOwnRelationControl = !isOwnPage && !!this.user && !!app.session.user && (this.user.attribute('friendshipIsFriend') === true || app.forum.attribute('canAddFriends') === true);
    return m("div", {
      className: "FriendsPage"
    }, m("div", {
      className: "FriendsPage-header"
    }, m("h2", null, app.translator.trans('forumaker-friendship.forum.user.friends_count', {
      count: this.totalCount
    })), m("div", {
      className: "FriendsPage-headerActions"
    }, showOwnRelationControl && this.user && m(Button, {
      className: "Button",
      icon: this.user.attribute('friendshipIsFriend') === true ? 'fas fa-user-minus' : this.user.attribute('friendshipHasPendingOutgoing') === true ? 'fas fa-ban' : 'fas fa-user-plus',
      onclick: () => showFriendActionModal(this.user, {
        onChange: () => m.redraw()
      })
    }, app.translator.trans(this.user.attribute('friendshipIsFriend') === true ? 'forumaker-friendship.forum.dropdown.remove_friend_btn' : this.user.attribute('friendshipHasPendingOutgoing') === true ? 'forumaker-friendship.forum.dropdown.cancel_request_btn' : 'forumaker-friendship.forum.dropdown.add_friend_btn')), canOpenRequests && this.user && m(Button, {
      className: "Button Button--primary",
      icon: "fas fa-user-clock",
      onclick: () => this.showRequestsModal()
    }, (() => {
      const incoming = this.user.attribute('friendshipIncomingRequestCount') || 0;
      return incoming > 0 ? app.translator.trans('forumaker-friendship.forum.user.requests_btn_count', {
        count: incoming
      }) : app.translator.trans('forumaker-friendship.forum.user.requests_btn');
    })()))), m("input", {
      type: "text",
      className: "FormControl FriendsPage-search",
      placeholder: String(app.translator.trans('forumaker-friendship.forum.requests_modal.search_placeholder')),
      value: this.search,
      oninput: e => this.onsearch(e.target.value)
    }), this.friends.length === 0 ? m("div", {
      className: "FriendsPage-emptyState"
    }, m("i", {
      className: "fas fa-user-friends FriendsPage-emptyIcon"
    }), m("p", null, app.translator.trans('forumaker-friendship.forum.user.no_friends'))) : m("ul", {
      className: "FriendsPage-grid"
    }, this.friends.map(friendship => {
      const friend = friendship.friend();
      if (!friend) return null;
      return m("li", {
        className: "FriendsPage-item",
        key: friendship.id()
      }, m(Link, {
        href: app.route.user(friend),
        className: "FriendsPage-itemLink"
      }, m(Avatar, {
        user: friend
      }), m("div", {
        className: "FriendsPage-itemInfo"
      }, m("span", {
        className: "FriendsPage-itemName"
      }, username(friend)), m("span", {
        className: "FriendsPage-itemDate"
      }, app.translator.trans('forumaker-friendship.forum.user.friends_since', {
        date: dayjs(friendship.createdAt()).format('D MMMM YYYY')
      })))), canRemove && m(Button, {
        className: "Button Button--icon Button--flat FriendsPage-itemRemove",
        icon: "fas fa-user-minus",
        "aria-label": app.translator.trans('forumaker-friendship.forum.dropdown.remove_friend_btn'),
        onclick: () => app.modal.show(RemoveFriendConfirmModal, {
          user: friend,
          friendship,
          onRemoved: () => this.loadFriends()
        })
      }));
    })), this.hasMore && m("div", {
      className: "FriendsPage-loadMore"
    }, m(Button, {
      className: "Button",
      loading: this.loadingMore,
      onclick: () => this.loadFriends(true)
    }, app.translator.trans('forumaker-friendship.forum.user.load_more'))));
  }
  showRequestsModal(highlightRequestId) {
    if (!this.user) return;
    app.modal.show(RequestsModal, {
      user: this.user,
      onChange: () => this.loadFriends(),
      highlightRequestId
    });
  }
}
flarum.reg.add('forumaker-friendship', 'forum/components/FriendsPage', FriendsPage);